import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkflow } from "../../workflow/WorkflowContext";
import { UploadPanel } from "../../components/UploadPanel";
import { CalculatePanel } from "../../components/CalculatePanel";
import { LockedCalcNote, StaleInputsNote } from "../../components/LockedCalcNote";

/**
 * Step 1 — Upload (fully migrated to URL routing, v0.6 nav refactor).
 *
 * First step converted to a dedicated route component (pattern to be
 * replicated for Steps 2–6). Behaviour is identical to the legacy
 * Dashboard section: upload panel + calculate panel + a validated
 * Continue that only appears once Step 1 is complete. The Continue
 * button navigates via setActiveStep() which is now URL-based
 * (WorkflowContext → navigate('/workflow/<slug>')); the SAME
 * completion gate (state.steps[1].complete) is enforced first.
 */
export default function Step1Upload() {
  const { setActiveStep, state, refreshRuns, calc } = useWorkflow();
  const { t } = useTranslation();
  // A2-467 — the upload that just arrived, so the list can frame it and say
  // what to do next (operator 2026-10-20: easy to miss, unclear what to click).
  const [justUploadedId, setJustUploadedId] = useState(null);
  const onIngested = (res) => { setJustUploadedId(res?.run?.id || null); refreshRuns(); };
  return (
    <section data-testid="step-1-upload" className="space-y-3">
      {/* A2-537: a locked calc's Step 1 looked open for uploads. Uploading
          and running stay allowed, but they start a separate calculation;
          the filed one changes only by superseding it on Step 6. */}
      <LockedCalcNote calc={calc} step={1} />
      <StaleInputsNote calc={calc} derived={state.derived} step={1} />
      {/* A2-69 (2026-10-04) — one panel, in working order: client,
          file, uploads, period, run. */}
      <CalculatePanel uploadSlot={<UploadPanel onIngested={onIngested} />} justUploadedId={justUploadedId} />
      {state.steps[1].complete && (
        <button
          type="button"
          data-testid="step-1-continue"
          onClick={() => setActiveStep(2)}
          className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-4 py-2 tabular-nums text-xs rounded-xl"
        >
          {t("ui.dashboard.continue_to_step", { idx: 2, label: t("ui.stepper.step2") })}
        </button>
      )}
    </section>
  );
}
